import { useCallback, useMemo } from "react";
import { CustomFeaturesModalTableColumns } from "./CustomFeaturesModalTableColumns";
import { useFilters, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";
import {
  CircleIconButton,
  FlexContainers,
  Paragraph2,
  SVG,
  Table,
  WrapperContainer
} from "../../../../../../../components";
import { log, notificationService, t } from "../../../../../../../services";
import { strCode } from "../../../../../../../consts";

const CustomFeaturesModalTable = ({
  customFeatures,
  editingCustomFeature,
  setEditingCustomFeature,
  onAddCustomFeature,
  onSaveCustomFeatureEditing,
  onCancelCustomFeatureEditing,
  onDeleteCustomFeature,
  onSetEditingCustomFeature
}) => {
  const columns = useMemo(() => CustomFeaturesModalTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data: customFeatures
    },
    useFilters,
    useSortBy
  );

  const getEditingCustomFeature = useCallback(() => {
    return editingCustomFeature;
  }, [editingCustomFeature]);

  const onAddNewCustomFeature = () => {
    onAddCustomFeature({
      name: "",
      value: "",
      isEdit: true
    });
  };

  const onSaveCustomFeature = (name) => {
    try {
      onSaveCustomFeatureEditing(name);
    } catch (err) {
      log.error("FeaturesModalTable: Failed to save a feature", err);
      if (err.message === strCode.APP_UPDATE_ERROR_TITLE) {
        notificationService.showError({
          title: t(strCode.APP_FEATURE_UPDATE_ERROR_TITLE),
          message: t(strCode.SETTING_NAME_ERROR_TITLE)
        });
      } else {
        notificationService.showError({
          title: t(strCode.APP_UPDATE_ERROR_TITLE),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      }
    }
  };

  return (
    <WrapperContainer width={730}>
      <Table
        tableInstance={tableInstance}
        maxTableHeight="108px"
        component={
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            gap={16}
            height={customFeatures.length ? 60 : 82}
            mt={customFeatures.length ? 0 : 24}>
            {!customFeatures.length && <Paragraph2>{t(strCode.NO_CUSTOM_FEATURES_YET)}</Paragraph2>}

            <CircleIconButton
              onClick={onAddNewCustomFeature}
              disabled={
                Boolean(customFeatures.some((feature) => feature.isEdit)) ||
                customFeatures.length >= 15
              }
              tooltipLabel={t(strCode.ADD_FEATURE_TITLE)}>
              <SVG.PlusIcon />
            </CircleIconButton>
          </WrapperContainer>
        }
        componentHeight={64}
        maxVisibleRows={1}
        callbackFn1={getEditingCustomFeature}
        callbackFn2={onSaveCustomFeature}
        callbackFn3={onCancelCustomFeatureEditing}
        callbackFn4={setEditingCustomFeature}
        callbackFn5={onDeleteCustomFeature}
        callbackFn6={onSetEditingCustomFeature}
        modalTable
        showTableBorder
      />
    </WrapperContainer>
  );
};

CustomFeaturesModalTable.propTypes = {
  customFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  editingCustomFeature: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  }).isRequired,
  setEditingCustomFeature: PropTypes.func.isRequired,
  onAddCustomFeature: PropTypes.func.isRequired,
  onSaveCustomFeatureEditing: PropTypes.func.isRequired,
  onCancelCustomFeatureEditing: PropTypes.func.isRequired,
  onDeleteCustomFeature: PropTypes.func.isRequired,
  onSetEditingCustomFeature: PropTypes.func.isRequired
};

export default CustomFeaturesModalTable;
